/**
 * Types for Crypto Market Intelligence (v3.0)
 */

export type SectorStage =
  | 'inception'
  | 'early_growth'
  | 'growth'
  | 'mature'
  | 'decline'
  | 'unknown'

export type EntryTiming = 'NOW' | 'SOON' | 'WAIT' | 'LATE' | 'UNKNOWN'

export type AlertPriority = 'low' | 'medium' | 'high' | 'critical'

export type GrowthVelocity = 'slow' | 'moderate' | 'fast' | 'explosive'

export type RiskLevel = 'low' | 'medium' | 'high'

export interface CryptoSector {
  id: number
  name: string
  slug: string
  description: string | null
  keywords: string[]
  stage: SectorStage | null
  is_active: boolean
  created_at: string
}

export interface SectorSignal {
  date: string
  opportunity_score: number | null
  growth_velocity: GrowthVelocity | null
  risk_level: RiskLevel | null

  // On-chain (Dune)
  dune_volume_usd: number | null
  dune_volume_change_pct: number | null
  dune_active_wallets: number | null
  dune_tvl_usd: number | null

  // Social (X)
  x_mentions_count: number | null
  x_sentiment_score: number | null

  // Community (Telegram)
  telegram_channels_count: number | null
  telegram_new_channels_7d: number | null
}

export interface SectorOpportunity {
  sector: CryptoSector
  latest_signal: SectorSignal | null
  entry_timing: EntryTiming
  recommendation: string | null
}

export interface CryptoProtocol {
  id: number
  name: string
  slug: string
  blockchain: string | null
  website_url: string | null
  twitter_handle: string | null
  is_competitor: boolean
}

export interface GeoOpportunity {
  country_code: string
  country_name: string
  growth_score: number | null
  adoption_stage: string | null
  active_wallets: number | null
  wallets_change_pct: number | null
}

export interface MarketAlert {
  id: number
  alert_type: string
  title: string
  description: string | null
  priority: AlertPriority
  is_read: boolean
  created_at: string
  sector_name: string | null
}

export interface TrackSectorRequest {
  alert_threshold: number
  notify_on_stage_change: boolean
}

export interface IntegrationStatus {
  dune_configured: boolean
  dune_api_key_set: boolean
  x_configured: boolean
  x_bearer_token_set: boolean
  telegram_configured: boolean
  feature_enabled: boolean
}

// Helper functions
export function getStageLabel(stage: SectorStage | null): string {
  const labels: Record<SectorStage, string> = {
    inception: 'Зарождение',
    early_growth: 'Ранний рост',
    growth: 'Рост',
    mature: 'Зрелость',
    decline: 'Спад',
    unknown: 'Неизвестно',
  }
  return stage ? labels[stage] : 'Неизвестно'
}

export function getStageColor(stage: SectorStage | null): string {
  const colors: Record<SectorStage, string> = {
    inception: 'bg-purple-100 text-purple-700',
    early_growth: 'bg-green-100 text-green-700',
    growth: 'bg-blue-100 text-blue-700',
    mature: 'bg-yellow-100 text-yellow-700',
    decline: 'bg-red-100 text-red-700',
    unknown: 'bg-gray-100 text-gray-700',
  }
  return stage ? colors[stage] : colors.unknown
}

export function getEntryTimingLabel(timing: EntryTiming): string {
  const labels: Record<EntryTiming, string> = {
    NOW: 'Сейчас',
    SOON: 'Скоро',
    WAIT: 'Подождать',
    LATE: 'Поздно',
    UNKNOWN: '—',
  }
  return labels[timing]
}

export function getEntryTimingColor(timing: EntryTiming): string {
  const colors: Record<EntryTiming, string> = {
    NOW: 'bg-green-500 text-white',
    SOON: 'bg-blue-500 text-white',
    WAIT: 'bg-yellow-500 text-white',
    LATE: 'bg-red-500 text-white',
    UNKNOWN: 'bg-gray-300 text-gray-700',
  }
  return colors[timing]
}

export function getPriorityColor(priority: AlertPriority): string {
  const colors: Record<AlertPriority, string> = {
    critical: 'bg-red-100 text-red-700 border-red-300',
    high: 'bg-orange-100 text-orange-700 border-orange-300',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    low: 'bg-gray-100 text-gray-700 border-gray-300',
  }
  return colors[priority]
}

export function formatLargeNumber(num: number | null): string {
  if (num === null) return '—'
  if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(1)}B`
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`
  if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`
  return `$${num.toFixed(0)}`
}

export function formatPercentChange(pct: number | null): string {
  if (pct === null) return '—'
  const sign = pct >= 0 ? '+' : ''
  return `${sign}${pct.toFixed(1)}%`
}
